import { useTranslation } from 'react-i18next';
import { formatCentsForReport } from '@/domain/money';
import { formatReportDate, formatShortDate } from '@/lib/dateUtils';
import type { CollectionReport } from '@/domain/types';

/**
 * The printed collection summary sheet, reproducing the client's pre-printed
 * A4 landscape form.
 *
 * The row data arrives already laid out by the server — receipt number and
 * customer name only on the first line of a block, cheques placed from the
 * block's last invoice row downward, and the Cash column derived by the
 * cash-first waterfall. Rendering the same rows the PDF renders is what
 * guarantees the browser print and the downloaded PDF cannot disagree.
 *
 * Column headings stay English in both locales: this is a formal document that
 * has to look the same whoever prints it.
 */
export function CollectionSheet({ report }: { report: CollectionReport }): JSX.Element {
  const { t } = useTranslation();
  const s = (key: string) => t(`report.sheet.${key}` as never);

  return (
    <div className="print-sheet overflow-x-auto rounded-lg border border-slate-300 bg-white p-5">
      <header className="text-center">
        <img src="/tharanga-logo.png" alt="Tharanga Steel" className="mx-auto h-14 w-auto object-contain" />
        <p className="text-[11px] text-slate-700">No. 526, Koswadiya, Mahawewa.</p>
        <p className="text-[11px] text-slate-700">Office Tel : 032 20 59 447</p>
        <p className="mt-2 text-sm font-bold tracking-wide">{s('heading')}</p>
      </header>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <table className="border-collapse text-xs">
          <tbody>
            <tr>
              <td className="border border-slate-700 px-2 py-1 font-semibold">{s('date')}</td>
              <td className="min-w-56 border border-slate-700 px-2 py-1">
                {formatReportDate(report.date)}
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 px-2 py-1 font-semibold">{s('estName')}</td>
              <td className="border border-slate-700 px-2 py-1">{report.agentName}</td>
            </tr>
          </tbody>
        </table>

        <p className="text-xs font-semibold">
          {s('serialNo')} : <span className="font-normal">{report.serialNo}</span>
        </p>
      </div>

      <table className="mt-3 w-full border-collapse">
        <thead>
          <tr>
            <th className="w-16">{s('receiptNo')}</th>
            <th className="w-40">{s('customerName')}</th>
            <th className="w-20">{s('invoiceNo')}</th>
            <th className="w-20">{s('invoiceDate')}</th>
            <th className="w-20">{s('createdDate')}</th>
            <th className="w-24">{s('invoiceAmount')}</th>
            <th className="w-24">{s('cash')}</th>
            <th className="w-24">{s('cheque')}</th>
            <th className="w-24">{s('chequeNo')}</th>
            <th className="w-20">{s('chequeDate')}</th>
            <th className="w-20">{s('bank')}</th>
          </tr>
        </thead>

        <tbody>
          {report.rows.length === 0 && (
            <tr>
              <td colSpan={11} className="py-6 text-center text-slate-500">
                {t('report.noData')}
              </td>
            </tr>
          )}

          {report.rows.map((row, index) => (
            <tr
              key={index}
              // A heavier top border marks where a new receipt begins, the way
              // the clerk rules off each block on paper.
              className={row.isBlockStart ? 'print-block border-t-2 border-t-slate-700' : undefined}
            >
              <td className="tabular text-center">{row.receiptNo ?? ''}</td>
              <td>{row.customerName ?? ''}</td>
              <td className="tabular text-center">{row.invoiceNo ?? ''}</td>
              {/* nowrap: browsers may otherwise break a date at its hyphens. */}
              <td className="tabular whitespace-nowrap text-center">{row.invoiceDate ?? ''}</td>
              <td className="tabular whitespace-nowrap text-center">{row.createdDate ?? ''}</td>
              <td className="tabular text-right">{formatCentsForReport(row.invoiceAmountCents)}</td>
              <td className="tabular text-right">{formatCentsForReport(row.cashCents)}</td>
              <td className="tabular text-right">{formatCentsForReport(row.chequeAmountCents)}</td>
              <td className="tabular text-center">{row.chequeNo ?? ''}</td>
              <td className="text-center">{formatShortDate(row.chequeDate)}</td>
              <td className="text-center">{row.bank ?? ''}</td>
            </tr>
          ))}

          <tr className="border-t-2 border-t-slate-700 font-bold">
            <td colSpan={6} className="text-right">
              {s('total')}
            </td>
            <td className="tabular text-right">{formatCentsForReport(report.totals.cashCents)}</td>
            <td className="tabular text-right">{formatCentsForReport(report.totals.chequeCents)}</td>
            <td colSpan={3} />
          </tr>
        </tbody>
      </table>

      <div className="mt-8 flex justify-between gap-6 text-[11px]">
        {[s('collectedBy'), s('checkedBy'), s('approvedBy')].map((label) => (
          <div key={label} className="flex-1 text-center">
            <div className="mb-1 border-b border-slate-600" />
            <p>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
